// Kundenprofil → Notizen: Timeline mit Zeitstempel, Autor, Kategorie,
// Anpinnen und Sichtbarkeit (intern / für Kunde sichtbar).
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, Pin, PinOff, Trash2, StickyNote, Send, History, ChevronDown, Pencil, Check, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { value: 'general', label: 'Allgemein', color: 'bg-gray-100 text-gray-700' },
  { value: 'health', label: 'Gesundheit', color: 'bg-red-100 text-red-700' },
  { value: 'payment', label: 'Zahlung', color: 'bg-blue-100 text-blue-700' },
  { value: 'sales', label: 'Verkauf', color: 'bg-green-100 text-green-700' },
];
const catMeta = (v) => CATEGORIES.find((c) => c.value === v) || CATEGORIES[0];

const fmt = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return `${d.toLocaleDateString('de-CH')}, ${d.toLocaleTimeString('de-CH', { hour: '2-digit', minute: '2-digit' })}`;
};

export const CustomerNotesTab = ({ customerId }) => {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState('');
  const [category, setCategory] = useState('general');
  const [customerVisible, setCustomerVisible] = useState(false);
  const [pinned, setPinned] = useState(false);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState('all');
  const [canHealth, setCanHealth] = useState(false);
  const [openHistory, setOpenHistory] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');
  const [editCategory, setEditCategory] = useState('general');

  const startEdit = (n) => { setEditingId(n.id); setEditText(n.text); setEditCategory(n.category); };
  const cancelEdit = () => { setEditingId(null); setEditText(''); };
  const saveEdit = async (n) => {
    if (!editText.trim()) { toast.error('Text darf nicht leer sein'); return; }
    try {
      await axios.put(`${API}/customers/${customerId}/notes/${n.id}`, { text: editText.trim(), category: editCategory });
      toast.success('Notiz aktualisiert');
      cancelEdit();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Speichern fehlgeschlagen');
    }
  };

  const availableCategories = canHealth ? CATEGORIES : CATEGORIES.filter((c) => c.value !== 'health');

  const load = useCallback(async () => {
    if (!customerId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/customers/${customerId}/notes`);
      setNotes(res.data?.notes || []);
      const ch = res.data?.can_view_health !== false;
      setCanHealth(ch);
      if (!ch) {
        setCategory((c) => (c === 'health' ? 'general' : c));
        setFilter((f) => (f === 'health' ? 'all' : f));
      }
    } catch (e) {
      toast.error('Notizen konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!text.trim()) return;
    setSaving(true);
    try {
      await axios.post(`${API}/customers/${customerId}/notes`, {
        text: text.trim(), category, pinned,
        visibility: customerVisible ? 'customer' : 'internal',
      });
      setText(''); setCategory('general'); setCustomerVisible(false); setPinned(false);
      toast.success('Notiz gespeichert');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const togglePin = async (n) => {
    try { await axios.put(`${API}/customers/${customerId}/notes/${n.id}`, { pinned: !n.pinned }); load(); }
    catch { toast.error('Anpinnen fehlgeschlagen'); }
  };

  const del = async (n) => {
    if (!window.confirm('Notiz löschen?')) return;
    try { await axios.delete(`${API}/customers/${customerId}/notes/${n.id}`); toast.success('Gelöscht'); load(); }
    catch { toast.error('Löschen fehlgeschlagen'); }
  };

  const visible = filter === 'all' ? notes : notes.filter((n) => n.category === filter);

  return (
    <div className="space-y-5" data-testid="notes-tab">
      <div className="rounded-lg border bg-muted/30 p-4 space-y-3" data-testid="note-composer">
        <textarea
          className="w-full min-h-[70px] rounded-md border border-input bg-background px-3 py-2 text-sm"
          placeholder="Neue Notiz … (Datum, Uhrzeit & Autor werden automatisch gespeichert)"
          value={text} onChange={(e) => setText(e.target.value)}
          data-testid="note-text-input"
        />
        <div className="flex flex-wrap items-center gap-3">
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="w-40" data-testid="note-category-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {availableCategories.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <label className="flex items-center gap-2 text-sm"><Switch checked={pinned} onCheckedChange={setPinned} data-testid="note-pin-toggle" /> Anpinnen</label>
          <label className="flex items-center gap-2 text-sm"><Switch checked={customerVisible} onCheckedChange={setCustomerVisible} data-testid="note-visibility-toggle" /> Für Kunde sichtbar</label>
          <Button onClick={add} disabled={saving || !text.trim()} className="ml-auto" data-testid="note-save-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
            Speichern
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="note-filters">
        {[{ value: 'all', label: 'Alle' }, ...availableCategories].map((c) => (
          <button key={c.value} onClick={() => setFilter(c.value)}
            className={`px-3 py-1 rounded-full text-sm border transition-colors ${filter === c.value ? 'bg-primary text-primary-foreground border-primary' : 'bg-background hover:bg-muted'}`}
            data-testid={`note-filter-${c.value}`}>
            {c.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-muted-foreground p-6"><Loader2 className="w-4 h-4 animate-spin" /> Lädt …</div>
      ) : !visible.length ? (
        <div className="p-8 text-center text-muted-foreground" data-testid="notes-empty">
          <StickyNote className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p>Noch keine Notizen.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="notes-list">
          {visible.map((n) => {
            const cm = catMeta(n.category);
            return (
              <div key={n.id} className={`rounded-lg border p-3 ${n.pinned ? 'border-amber-300 bg-amber-50/40' : 'bg-background'}`} data-testid={`note-item-${n.id}`}>
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <Badge className={`${cm.color} border-0`}>{cm.label}</Badge>
                  {n.visibility === 'customer' && <Badge variant="outline" className="text-xs">Für Kunde sichtbar</Badge>}
                  {n.pinned && <Pin className="w-3.5 h-3.5 text-amber-600" />}
                  <span className="text-xs text-muted-foreground ml-auto" data-testid={`note-meta-${n.id}`}>{fmt(n.created_at)} · {n.author_name}</span>
                </div>
                {editingId === n.id ? (
                  <div className="space-y-2" data-testid={`note-edit-${n.id}`}>
                    <textarea className="w-full min-h-[60px] rounded-md border border-input bg-background px-3 py-2 text-sm"
                      value={editText} onChange={(e) => setEditText(e.target.value)} data-testid={`note-edit-text-${n.id}`} />
                    <div className="flex items-center gap-2">
                      <Select value={editCategory} onValueChange={setEditCategory}>
                        <SelectTrigger className="w-40" data-testid={`note-edit-category-${n.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {availableCategories.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <Button size="sm" onClick={() => saveEdit(n)} data-testid={`note-edit-save-${n.id}`}><Check className="w-3.5 h-3.5 mr-1" /> Speichern</Button>
                      <Button size="sm" variant="ghost" onClick={cancelEdit} data-testid={`note-edit-cancel-${n.id}`}><X className="w-3.5 h-3.5" /></Button>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm whitespace-pre-wrap">{n.text}</p>
                )}
                {(n.history || []).length > 0 && (
                  <div className="mt-2" data-testid={`note-history-${n.id}`}>
                    <button
                      className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                      onClick={() => setOpenHistory((p) => ({ ...p, [n.id]: !p[n.id] }))}
                      data-testid={`note-history-toggle-${n.id}`}
                    >
                      <History className="w-3 h-3" />
                      Bearbeitet ({n.history.length})
                      <ChevronDown className={`w-3 h-3 transition-transform ${openHistory[n.id] ? 'rotate-180' : ''}`} />
                    </button>
                    {openHistory[n.id] && (
                      <ul className="mt-1 space-y-1 border-l-2 border-muted pl-2">
                        {n.history.slice().reverse().map((h, hi) => (
                          <li key={hi} className="text-xs text-muted-foreground">
                            <span className="font-medium text-foreground/70">{fmt(h.at)} · {h.by}</span>{' — '}
                            {(h.changes || []).map((c) => c.label).join(', ')} geändert
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
                <div className="flex gap-1 mt-2">
                  <Button variant="ghost" size="sm" onClick={() => startEdit(n)} data-testid={`note-edit-btn-${n.id}`}>
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => togglePin(n)} data-testid={`note-pin-btn-${n.id}`}>
                    {n.pinned ? <PinOff className="w-3.5 h-3.5" /> : <Pin className="w-3.5 h-3.5" />}
                  </Button>
                  <Button variant="ghost" size="sm" className="text-red-600" onClick={() => del(n)} data-testid={`note-delete-btn-${n.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CustomerNotesTab;
